import { createContext, useState, useContext, useEffect } from "react";
import { isFeatureActive } from "../utils/secretCodes";

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage (fallback to "dark").
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("theme") || "dark";
  });

  // Check which features/modes are unlocked
  const isDonnieModeUnlocked = isFeatureActive("DONNIE_MODE");
  const isMojoModeUnlocked = isFeatureActive("MOJO_MODE");
  const isJusticeModeUnlocked = isFeatureActive("JUSTICE_MODE");
  const isOwenModeUnlocked = isFeatureActive("OWEN_MODE");
  const isHanukkahModeUnlocked = isFeatureActive("HANUKKAH_MODE");

  // Ordered list of possible themes
  const themesSequence = [
    "light",
    "dark",
    "donnie",
    "mojo",
    "justice",
    "owen",
    "hanukkah",
  ];

  // Helper to check if a given theme is unlocked
  const isThemeUnlocked = (themeName) => {
    if (themeName === "light" || themeName === "dark") return true;
    if (themeName === "donnie") return isDonnieModeUnlocked;
    if (themeName === "mojo") return isMojoModeUnlocked;
    if (themeName === "justice") return isJusticeModeUnlocked;
    if (themeName === "owen") return isOwenModeUnlocked;
    if (themeName === "hanukkah") return isHanukkahModeUnlocked;
    return false;
  };

  // Function to find the next unlocked theme
  const getNextTheme = (currentTheme) => {
    // Current index in the sequence
    let currentIndex = themesSequence.indexOf(currentTheme);

    // If current theme is somehow not in the list, default to 0
    if (currentIndex < 0) {
      currentIndex = 0;
    }

    // We'll look for the next unlocked theme in the sequence (circular)
    let nextIndex = (currentIndex + 1) % themesSequence.length;

    // Loop through all themes (at most once)
    for (let i = 0; i < themesSequence.length; i++) {
      const candidate = themesSequence[nextIndex];
      if (isThemeUnlocked(candidate)) {
        return candidate;
      }
      nextIndex = (nextIndex + 1) % themesSequence.length;
    }

    // If none found (shouldn't happen if at least light/dark are unlocked), fallback:
    return "light";
  };

  // Toggle to the next unlocked theme
  const toggleTheme = () => {
    setTheme((current) => getNextTheme(current));
  };

  // Whenever `theme` changes, update localStorage and <html> class
  useEffect(() => {
    localStorage.setItem("theme", theme);

    // Remove all possible theme classes from <html>
    document.documentElement.classList.remove(
      "light",
      "dark",
      "donnie",
      "mojo",
      "justice",
      "owen",
      "hanukkah"
    );

    // Add the current theme class to <html>
    document.documentElement.classList.add(theme);
  }, [theme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
        isDonnieModeUnlocked,
        isMojoModeUnlocked,
        isJusticeModeUnlocked,
        isOwenModeUnlocked,
        isHanukkahModeUnlocked,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  return useContext(ThemeContext);
};
